import { useRef, useState, useEffect } from 'react';
import VideoCard from '@/components/smartpos/VideoCard';
import VideoPlayer from '@/components/smartpos/VideoPlayer';
import { Image } from '@/components/ui/image';

const ACCENTS = {
  blue: { badge: 'bg-blue-50 border-blue-200 text-brand-blue', shadow: 'shadow-blue-500/10' },
  green: { badge: 'bg-green-50 border-green-200 text-brand-green', shadow: 'shadow-green-500/10' },
  violet: { badge: 'bg-violet-50 border-violet-200 text-brand-violet', shadow: 'shadow-violet-500/10' },
};

const DOT_COLORS = ['#0ff0b3', '#0095fc', '#7800f7'];

function useReveal(ref) {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setRevealed(true); }, { threshold: 0.1 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return revealed;
}

export default function SolutionHero({
  eyebrow,
  accent = 'blue',
  title,
  titleHighlight,
  subtitle,
  videoId,
  videoSrc,
  image,
  mockup,
  windowUrl = 'app.smartpos.es',
  benefits = [],
  layout = 'default',
}) {
  const ref = useRef(null);
  const revealed = useReveal(ref);
  const a = ACCENTS[accent] || ACCENTS.blue;
  const centered = layout === 'centered' || layout === 'visual-top' || layout === 'split-bottom';

  const renderVisual = () => mockup ? mockup : (
    <div className={`rounded-2xl overflow-hidden border border-gray-200 shadow-xl ${a.shadow}`}>
      <div className="bg-gray-900 px-4 py-2.5 flex items-center gap-2 border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full" style={{ background: DOT_COLORS[0] }} />
          <div className="w-3 h-3 rounded-full" style={{ background: DOT_COLORS[1] }} />
          <div className="w-3 h-3 rounded-full" style={{ background: DOT_COLORS[2] }} />
        </div>
        <div className="flex-1 mx-3 bg-white/10 rounded-md px-3 py-1 text-white/40 text-xs font-medium border border-white/10">
          {windowUrl}
        </div>
      </div>
      {videoId ? (
        <VideoCard videoId={videoId} aspectClass="aspect-video" className="rounded-none border-0" />
      ) : videoSrc ? (
        <VideoPlayer src={videoSrc} className="w-full" />
      ) : image ? (
        <Image src={image} alt={title} fittingType="fill" priority quality={75} className="w-full aspect-video" />
      ) : null}
    </div>
  );

  const textBlock = (
    <div className={`transition-all duration-700 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${centered ? 'text-center' : ''}`}>
      <div className={centered ? 'max-w-3xl mx-auto' : ''}>
        <span className={`inline-block text-xs font-black tracking-widest uppercase mb-4 px-3 py-1 rounded-full border ${a.badge}`}>
          {eyebrow}
        </span>
        <h1 className="font-heading font-semibold text-3xl lg:text-4xl xl:text-5xl text-gray-900 leading-[1.1] mb-4 tracking-tight text-balance">
          {title}
          <br />
          <span className="brand-gradient-text font-medium">{titleHighlight}</span>
        </h1>
        <p className="text-gray-500 text-lg font-light leading-relaxed">
          {subtitle}
        </p>
      </div>
    </div>
  );

  const visualBlock = (
    <div className={`relative transition-all duration-700 delay-150 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {renderVisual()}
    </div>
  );

  return (
    <section ref={ref} className="pt-32 pb-20 lg:pt-44 lg:pb-32 bg-white relative overflow-hidden flex items-center min-h-[80vh]">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: 'linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 80%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {layout === 'mirror' && (
          <>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {visualBlock}
              {textBlock}
            </div>
          </>
        )}

        {layout === 'centered' && (
          <>
            {textBlock}
            <div className="mt-10 max-w-3xl mx-auto">{visualBlock}</div>
          </>
        )}

        {layout === 'inline' && (
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {textBlock}
            </div>
            {visualBlock}
          </div>
        )}

        {layout === 'inline-mirror' && (
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {visualBlock}
            <div>
              {textBlock}
            </div>
          </div>
        )}

        {layout === 'visual-top' && (
          <>
            {visualBlock}
            <div className="mt-10">{textBlock}</div>
          </>
        )}

        {layout === 'split-bottom' && (
          <>
            {textBlock}
            <div className="grid lg:grid-cols-2 gap-12 items-center mt-10">
              {visualBlock}
              <div></div>
            </div>
          </>
        )}

        {layout === 'default' && (
          <>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {textBlock}
              {visualBlock}
            </div>
          </>
        )}
      </div>
    </section>
  );
}